import { action, computed, observable } from 'mobx';
import { BaseBlock } from '~/models/blocks/base-block.model';
import { IImage } from '~/types/model.types';
import { IImageBlockResponse } from '~/types/server.types';
import type { RootStore } from '~/stores/root.store';
import type { ContainerBlock } from '~/models/blocks/container-block.model';
import type { FlowRevision } from '~/models/flow-revision.model';
import {
    Alignment,
    IImageBlockStyles,
    ContentWidth,
    PaddingSize
} from '~/types/block-styles.types';

type ImageBlockDataForServer = ReturnType<BaseBlock['getDataForServer']> &
    Pick<IImageBlockResponse, 'image' | 'type'>;

export class ImageBlock extends BaseBlock {
    type: 'image_block';
    @observable image: IImage;
    @observable styles: IImageBlockStyles;
    @observable isCanvaBlock: boolean;

    constructor(
        block: IImageBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore,
        initialAttributes: any
    ) {
        super(block, flowRevision, rootStore, initialAttributes);
        this.type = block.type;
        this.resizeable = true;
        this.image = block.image;
        this.styles = {
            ...this.styles,
            ...block.image_styles
        };
        this.isCanvaBlock = initialAttributes?.isCanvaBlock;
    }

    get isDeletable() {
        return true;
    }

    @computed
    get isStretched() {
        return this.styles.alignment === Alignment.STRETCH;
    }

    @computed
    get shouldExpandOnSelect() {
        return !this.isStretched;
    }

    currentSize() {
        return this.image
            ? {
                  image: {
                      ...this.image,
                      resize_ratio: this.image.resize_ratio
                  }
              }
            : null;
    }

    @action
    updateRatio(ratio: number) {
        this.image.resize_ratio = ratio;
    }

    @computed
    get parentWidth() {
        const horizontalParent =
            this.calculateRootLevelParent() as ContainerBlock;

        if (!horizontalParent) {
            return 0;
        }

        let verticalParent = horizontalParent?.children.find(
            ({ block_id }) => block_id === this.parent_id
        );

        if (!verticalParent) {
            verticalParent = this.parent?.parent?.children.find(
                ({ block_id }) => block_id === this.parent_id
            );
        }

        return verticalParent.size === 1
            ? 100 / horizontalParent?.children.length
            : verticalParent.size;
    }

    @computed
    get isImagePlaceholder() {
        return !this.image || (this.image && !this.image.url);
    }

    getDataForServer(): ImageBlockDataForServer {
        return {
            ...super.getDataForServer(),
            type: this.type as 'image_block',
            image: this.image ? { ...this.image } : null
        };
    }

    @computed
    get isImageFullWidth() {
        const imageBlockStretched = this.styles.alignment === Alignment.STRETCH;
        const parentBlock = this.calculateRootLevelParent();

        if (parentBlock) {
            const parentAllowsFullWith =
                parentBlock.styles.content_width === ContentWidth.FULL &&
                parentBlock.styles.padding_y === PaddingSize.CUSTOM &&
                parentBlock.styles.custom_padding_y === 0;

            return imageBlockStretched && parentAllowsFullWith;
        }

        return imageBlockStretched;
    }
}
